import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import countryList from "react-select-country-list";

import { IUserProfile, TUserGroup, TUserRank } from "MVVM/@types";
import { Avatar } from "MVVM/Components/AvatarV2";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { TeamBadge } from "MVVM/Components/Teams/TeamBadge";

interface IProfileCardHeaderProps {
  profileUser?: IUserProfile;
  username?: string;
  avatar?: string;
  userGroup?: TUserGroup;
  rank?: TUserRank;
  loading?: boolean;
}

export const ProfileCardHeader = ({
  profileUser,
  username,
  avatar,
  userGroup,
  rank,
  loading,
}: IProfileCardHeaderProps) => {
  const countryCode = profileUser?.profile?.profile?.countryCode;
  const primaryTeam = profileUser?.teams?.nodes?.[0];

  return (
    <HeaderSection>
      <AvatarSection>
        <Avatar
          src={avatar || profileUser?.avatarUrl}
          size="lg"
          userGroup={userGroup}
          rank={rank}
          link={`/u/${profileUser?.username || profileUser?._id}`}
        />
      </AvatarSection>
      <UserInfo ml={2}>
        <Flex alignItems="center" justifyContent="flex-start" mb={1}>
          <UserName>{profileUser?.username || username}</UserName>
        </Flex>
        <Flex style={{ gap: 8 }} alignItems="center" mb={2}>
          {primaryTeam?.tag && <TeamBadge team={primaryTeam} size="small" />}
          {countryCode && (
            <FlagContainer>
              <AnimatedTooltip content={countryList().getLabel(countryCode)}>
                <CountryFlag
                  src={`${
                    process.env.REACT_APP_S3_CLIP_BUCKET
                  }/static/web/flags/${countryCode.toLowerCase()}.png`}
                  alt={`${countryCode} flag`}
                />
              </AnimatedTooltip>
            </FlagContainer>
          )}
        </Flex>

        {loading ? (
          <LoadingStats>
            <LoadingDot />
            <LoadingDot />
            <LoadingDot />
          </LoadingStats>
        ) : (
          <FollowStats>
            <Text color="white" fontSize="14px" fontWeight="500">
              {profileUser?.followerCount || 0}{" "}
              <Text as="span" color="shade300">
                Followers
              </Text>
            </Text>
            <Text color="shade300" mx="8px">
              •
            </Text>
            <Text color="white" fontSize="14px" fontWeight="500">
              {profileUser?.followingCount || 0}{" "}
              <Text as="span" color="shade300">
                Following
              </Text>
            </Text>
          </FollowStats>
        )}
      </UserInfo>
    </HeaderSection>
  );
};

const FlagContainer = styled(Box)`
  align-items: center;
  display: inline-flex;
  position: relative;
`;

const AvatarSection = styled(Box)`
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  margin-top: 2px;
  position: relative;
  width: 88px;
  z-index: 20;
`;

const HeaderSection = styled(Flex)`
  align-items: center;
  gap: 16px;
  left: 20px;
  position: absolute;
  right: 20px;
  top: 22px;
  z-index: 3;
  overflow: visible;
`;

const UserInfo = styled(Flex)`
  align-items: flex-start;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
`;

const UserName = styled(Text)`
  color: ${({ theme }) => theme.colors.shade100};
  font-family: Barlow;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  leading-trim: both;
  line-height: 28px;
  text-align: left;
  text-edge: cap;
  word-break: break-word;
`;

const CountryFlag = styled.img`
  border-radius: 3px;
  cursor: pointer;
  display: block;
  filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.4));
  height: 15px;
  object-fit: cover;
  width: 20px;
`;

const LoadingStats = styled(Flex)`
  align-items: center;
  gap: 4px;
  justify-content: flex-start;
  margin: 8px 0;
`;

const LoadingDot = styled(Box)`
  animation: pulse 1.5s ease-in-out infinite;
  background: ${({ theme }) => theme.colors.shade300};
  border-radius: 50%;
  height: 6px;
  width: 6px;

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.4;
    }
    50% {
      opacity: 1;
    }
  }

  &:nth-child(2) {
    animation-delay: 0.2s;
  }

  &:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

const FollowStats = styled(Flex)`
  align-items: center;
  justify-content: flex-start;
`;
